import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { apiErr } from "@/lib/format";
import { toast } from "sonner";
import {
  PlusIcon, XIcon, PencilSimpleIcon, ClipboardTextIcon, FunnelSimpleIcon,
  ChartBarIcon, BookOpenIcon, BuildingOfficeIcon, ReceiptIcon,
} from "@phosphor-icons/react";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";

const RUBROS = [
  { k: "administrativos", l: "Administrativos", c: "text-blue-400 border-blue-500/30 bg-blue-500/10", i: BuildingOfficeIcon },
  { k: "ventas_comerciales", l: "Ventas y Comerciales", c: "text-agro-green border-agro-green/30 bg-agro-green/10", i: ChartBarIcon },
  { k: "financieros", l: "Financieros", c: "text-agro-yellow border-agro-yellow/30 bg-agro-yellow/10", i: ReceiptIcon },
  { k: "operativos_agro", l: "Operativos / Agro", c: "text-purple-400 border-purple-500/30 bg-purple-500/10", i: BookOpenIcon },
  { k: "importacion", l: "Importación", c: "text-cyan-400 border-cyan-500/30 bg-cyan-500/10", i: ReceiptIcon },
  { k: "generales", l: "Generales", c: "text-orange-400 border-orange-500/30 bg-orange-500/10", i: ClipboardTextIcon },
  { k: "otros", l: "Otros", c: "text-text-secondary border-border bg-app", i: ClipboardTextIcon },
];

export default function ExpenseAccounts() {
  const [grouped, setGrouped] = useState(null);
  const [accounts, setAccounts] = useState([]);  // chart of accounts (parent codes for select)
  const [suppliers, setSuppliers] = useState([]);
  const [filterRubro, setFilterRubro] = useState("");
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const [g, c, s] = await Promise.all([
      api.get("/accounting/expense-accounts/grouped"),
      api.get("/accounting/chart"),
      api.get("/suppliers").catch(() => ({ data: [] })),
    ]);
    setGrouped(g.data);
    setSuppliers(s.data || []);
    setAccounts(c.data.filter((x) => x.tipo === "gasto" || x.tipo === "costo"));
  };
  useEffect(() => { load(); }, []);

  const onSaved = () => { setOpenForm(false); setEditing(null); load(); };

  const totalPartidas = grouped?.total_partidas || 0;
  const totalRubros = grouped?.total_rubros || 0;

  return (
    <Layout
      title="Cuentas de Gasto (Partidas)"
      subtitle={`${totalPartidas} partidas activas · ${totalRubros} rubros · PRD 4.6`}
      actions={
        <button
          onClick={() => { setEditing(null); setOpenForm(true); }}
          data-testid="new-expense-account-btn"
          className="bg-agro-green hover:bg-agro-green-hover text-black font-medium rounded-md px-4 py-2 text-sm flex items-center gap-2"
        >
          <PlusIcon size={16} weight="bold" /> Nueva partida
        </button>
      }
    >
      {/* Filtro rubro */}
      <div className="flex items-center gap-2 mb-5 overflow-x-auto pb-1">
        <FunnelSimpleIcon size={14} className="text-text-muted shrink-0" />
        <button
          onClick={() => setFilterRubro("")}
          data-testid="filter-rubro-todos"
          className={`text-xs px-3 py-1.5 rounded-md whitespace-nowrap transition-all ${
            filterRubro === ""
              ? "bg-agro-green/15 text-agro-green border border-agro-green/30"
              : "bg-app border border-border text-text-secondary hover:text-text-primary"
          }`}
        >
          Todos
        </button>
        {RUBROS.map((r) => (
          <button
            key={r.k}
            onClick={() => setFilterRubro(r.k)}
            data-testid={`filter-rubro-${r.k}`}
            className={`text-xs px-3 py-1.5 rounded-md whitespace-nowrap transition-all border ${
              filterRubro === r.k
                ? r.c
                : "bg-app border-border text-text-secondary hover:text-text-primary"
            }`}
          >
            {r.l}
          </button>
        ))}
      </div>

      {/* Rubros */}
      {!grouped ? (
        <div className="text-text-muted text-sm">Cargando…</div>
      ) : (
        <div className="space-y-5">
          {RUBROS.filter((r) => !filterRubro || r.k === filterRubro).map((r) => {
            const items = grouped.rubros[r.k] || [];
            if (items.length === 0 && filterRubro !== r.k) return null;
            const Icon = r.i;
            return (
              <div key={r.k} className="bg-card border border-border/60 rounded-lg overflow-hidden" data-testid={`rubro-${r.k}`}>
                <div className={`p-4 border-b border-border/60 flex items-center justify-between`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-md flex items-center justify-center border ${r.c}`}>
                      <Icon size={16} weight="duotone" />
                    </div>
                    <div>
                      <h3 className="font-heading text-sm font-semibold uppercase tracking-wider text-text-primary">{r.l}</h3>
                      <div className="text-[10px] text-text-muted">{items.length} {items.length === 1 ? "partida" : "partidas"}</div>
                    </div>
                  </div>
                </div>
                <table className="w-full">
                  <thead className="bg-sidebar/40">
                    <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
                      <th className="p-2.5 pl-5">Código</th>
                      <th className="p-2.5">Nombre</th>
                      <th className="p-2.5">Cuenta contable</th>
                      <th className="p-2.5">Tipo</th>
                      <th className="p-2.5">Deducible</th>
                      <th className="p-2.5">% sug.</th>
                      <th className="p-2.5 pr-5 text-right"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="p-6 text-center text-text-muted text-sm">Sin partidas registradas</td>
                      </tr>
                    ) : items.map((it) => (
                      <tr key={it.id} className="border-t border-border/30 hover:bg-white/[0.02]" data-testid={`partida-${it.codigo}`}>
                        <td className="p-2.5 pl-5 font-mono text-xs text-agro-green">{it.codigo}</td>
                        <td className="p-2.5 text-sm text-text-primary">{it.nombre}</td>
                        <td className="p-2.5 font-mono text-xs text-text-secondary">{it.cuenta_contable || "—"}</td>
                        <td className="p-2.5"><TipoBadge t={it.tipo} /></td>
                        <td className="p-2.5">
                          {it.deducible
                            ? <span className="text-[10px] uppercase px-2 py-0.5 rounded bg-agro-green/10 text-agro-green border border-agro-green/30">Sí</span>
                            : <span className="text-[10px] uppercase px-2 py-0.5 rounded bg-red-500/10 text-red-400 border border-red-500/30">No</span>}
                        </td>
                        <td className="p-2.5 text-sm text-text-secondary">{it.porcentaje_sugerido}%</td>
                        <td className="p-2.5 pr-5 text-right">
                          <button
                            onClick={() => { setEditing(it); setOpenForm(true); }}
                            data-testid={`edit-partida-${it.codigo}`}
                            className="text-text-muted hover:text-agro-yellow p-1"
                            title="Editar"
                          >
                            <PencilSimpleIcon size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      )}

      {openForm && (
        <ExpenseAccountForm
          accounts={accounts}
          suppliers={suppliers}
          existing={editing}
          onClose={() => { setOpenForm(false); setEditing(null); }}
          onSaved={onSaved}
        />
      )}
    </Layout>
  );
}

function TipoBadge({ t }) {
  const map = {
    fijo: "bg-blue-500/10 text-blue-400 border-blue-500/30",
    variable: "bg-agro-green/10 text-agro-green border-agro-green/30",
    extraordinario: "bg-orange-500/10 text-orange-400 border-orange-500/30",
    operativo: "bg-purple-500/10 text-purple-400 border-purple-500/30",
    administrativo: "bg-cyan-500/10 text-cyan-400 border-cyan-500/30",
    de_ventas: "bg-agro-yellow/10 text-agro-yellow border-agro-yellow/30",
  };
  return <span className={`text-[10px] uppercase px-2 py-0.5 rounded border ${map[t] || ""}`}>{(t || "").replace("_", " ")}</span>;
}

function ExpenseAccountForm({ accounts, suppliers = [], existing, onClose, onSaved }) {
  const [form, setForm] = useState(existing || {
    codigo: "", nombre: "", rubro: "generales", cuenta_contable: "",
    tipo: "variable", deducible: true, porcentaje_sugerido: 100,
    proveedor_default_nombre: "", region_default: "", centro_costo: "",
  });

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, porcentaje_sugerido: Number(form.porcentaje_sugerido || 100) };
      if (existing) {
        await api.put(`/accounting/expense-accounts/${existing.id}`, payload);
        toast.success("Partida actualizada");
      } else {
        await api.post("/accounting/expense-accounts", payload);
        toast.success("Partida creada");
      }
      onSaved();
    } catch (er) { toast.error(apiErr(er)); }
  };

  const m = useModalForm({ value: form, onClose: onClose, onSave: submit });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...m.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="expense-account-modal">
        <div className="p-5 border-b border-border flex items-center justify-between sticky top-0 bg-card z-10">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-agro-yellow">PRD 4.6 · Cuenta de Gasto</div>
            <h3 className="font-heading text-lg font-semibold mt-0.5">{existing ? "Editar partida" : "Nueva partida"}</h3>
          </div>
          <button aria-label="Cerrar" onClick={m.cerrar} className="text-text-muted hover:text-text-primary"><XIcon size={20} /></button>
        </div>
        <form onSubmit={m.guardar} className="p-5 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Código *</label>
              <input
                data-testid="partida-codigo"
                value={form.codigo} disabled={!!existing}
                onChange={(e) => setForm({ ...form, codigo: e.target.value })}
                placeholder="ej. 6130"
                required
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm font-mono focus:border-agro-green disabled:opacity-60"
              />
            </div>
            <div className="col-span-2">
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Nombre *</label>
              <input
                data-testid="partida-nombre"
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                required
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Rubro *</label>
              <select
                data-testid="partida-rubro"
                value={form.rubro}
                onChange={(e) => setForm({ ...form, rubro: e.target.value })}
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
              >
                {RUBROS.map((r) => <option key={r.k} value={r.k}>{r.l}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Cuenta contable</label>
              <select
                data-testid="partida-cuenta"
                value={form.cuenta_contable}
                onChange={(e) => setForm({ ...form, cuenta_contable: e.target.value })}
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
              >
                <option value="">— Sin enlazar —</option>
                {accounts.map((a) => <option key={a.codigo} value={a.codigo}>{a.codigo} · {a.nombre}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Tipo</label>
              <select
                value={form.tipo}
                onChange={(e) => setForm({ ...form, tipo: e.target.value })}
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
              >
                <option value="fijo">Fijo</option>
                <option value="variable">Variable</option>
                <option value="operativo">Operativo</option>
                <option value="administrativo">Administrativo</option>
                <option value="de_ventas">De ventas</option>
                {form.tipo === "extraordinario" && <option value="extraordinario">Extraordinario (antiguo)</option>}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Deducible</label>
              <select
                value={form.deducible ? "1" : "0"}
                onChange={(e) => setForm({ ...form, deducible: e.target.value === "1" })}
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
              >
                <option value="1">Sí</option>
                <option value="0">No</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">% sugerido</label>
              <input
                type="number" step="1" min="0" max="100"
                value={form.porcentaje_sugerido}
                onChange={(e) => setForm({ ...form, porcentaje_sugerido: e.target.value })}
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Proveedor default</label>
              <select
                data-testid="partida-proveedor"
                value={form.proveedor_default_id || ""}
                onChange={(e) => {
                  const sp = suppliers.find((x) => x.id === e.target.value);
                  setForm({ ...form, proveedor_default_id: e.target.value || null, proveedor_default_nombre: sp ? sp.nombre : "" });
                }}
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
              >
                <option value="">— Ninguno —</option>
                {suppliers.map((sp) => <option key={sp.id} value={sp.id}>{sp.codigo} · {sp.nombre}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Región / Centro</label>
              <input
                value={form.region_default || ""}
                onChange={(e) => setForm({ ...form, region_default: e.target.value })}
                placeholder="Opcional"
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Centro de costo</label>
              <input
                value={form.centro_costo || ""}
                onChange={(e) => setForm({ ...form, centro_costo: e.target.value })}
                placeholder="Opcional"
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
              />
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={m.cerrar} className="flex-1 bg-app border border-border hover:bg-white/[0.03] text-text-primary rounded-md py-2.5 text-sm">Cancelar</button>
            <button disabled={m.saving}
              type="submit"
              data-testid="save-partida-btn"
              className="flex-1 bg-agro-green hover:bg-agro-green-hover text-black font-semibold rounded-md py-2.5 text-sm"
            >
              {existing ? "Guardar cambios" : "Crear partida"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
